import { useEffect, useRef } from 'react'
import { purchasers } from '../game/diff'
import type { GameState } from '../game/types'
import { setMood } from './music'
import { play, type SfxName } from './sfx'
import { speak, type Emotion } from './voice'

/**
 * เล่นเสียงจาก "ความต่างของสถานะเกม" ไม่ใช่จากปุ่มที่กด
 * ทำแบบนี้ได้เสียงครบทั้งโหมดเล่นเครื่องเดียวและโหมดออนไลน์
 * (ออนไลน์เรารับสถานะจากเซิร์ฟเวอร์ ไม่รู้ว่าใครกดอะไร) และได้ยินเสียงตอนบอทเล่นด้วย
 */
export function useGameSfx(g: GameState | null, mySeat: number | null): void {
  const prev = useRef<GameState | null>(null)

  // เพลงเปลี่ยนอารมณ์ตามสถานการณ์ — ตึงขึ้นเมื่อมีเหตุการณ์ร้ายระดับโลกค้างอยู่
  useEffect(() => {
    if (!g) return
    if (g.phase === 'gameover') setMood('victory')
    else if (g.globalEffects.some((e) => !e.good)) setMood('tense')
    else setMood('calm')
  }, [g])

  useEffect(() => {
    const before = prev.current
    prev.current = g
    if (!g) return
    if (!before || before.players.length !== g.players.length) return

    const queue: SfxName[] = []
    const add = (n: SfxName) => {
      if (!queue.includes(n)) queue.push(n)
    }

    // เสียงพูดตัวละคร — เก็บตามลำดับความสำคัญ ตัวไหนถูกใส่ก่อนได้อารมณ์นั้น
    // ใส่เรียงจากเรื่องใหญ่ไปเรื่องเล็ก แล้วค่อยตัดให้เหลือ 2 ตัวตอนท้าย
    const voices: Array<{ id: string; emotion: Emotion }> = []
    const say = (seat: number, emotion: Emotion) => {
      const id = g.players[seat]?.character.id
      if (!id || g.players[seat]?.bankrupt) return
      if (voices.some((v) => v.id === id)) return
      voices.push({ id, emotion })
    }

    /* ---- จบเกม ---- */
    if (before.phase !== 'gameover' && g.phase === 'gameover') {
      play('win')
      // ผู้ชนะดีใจ ที่เหลือเสียใจ
      const winner = g.players.findIndex((p) => !p.bankrupt)
      g.players.forEach((_, i) => say(i, i === winner ? 'happy' : 'sad'))
      voices.slice(0, 3).forEach((v) => speak(v.id, v.emotion))
      return
    }

    /* ---- ล้มละลาย ---- */
    const newlyBankrupt = g.players.some((p, i) => p.bankrupt && !before.players[i].bankrupt)
    if (newlyBankrupt) {
      add('bankrupt')
      // ยังพูดได้ก่อนออกจากเกม จึงอ่านจากสถานะเก่าที่ยังไม่ล้มละลาย
      before.players.forEach((p, i) => {
        if (g.players[i]?.bankrupt && !p.bankrupt && !voices.some((v) => v.id === p.character.id)) {
          voices.push({ id: p.character.id, emotion: 'sad' })
        }
      })
    }

    /* ---- ซื้อของ = ดีใจ ต้องมาก่อนเช็กยอดเงิน ---- */
    // ซื้อโฉนดเงินก็ลดเหมือนโดนรีดค่าเช่า ถ้าดูแค่ยอดเงินจะพูดเสียงเศร้าตอนซื้อของ
    // ใช้ตัวตัดสินเดียวกับฝั่งท่าทาง (game/diff.ts) ท่ากับเสียงจึงตรงกันเสมอ
    const bought = purchasers(before, g)
    g.players.forEach((p, i) => {
      if (bought.has(p.id)) say(i, 'happy')
    })

    /* ---- เงินเข้า/ออกรายคน — คนจ่ายเสียใจ คนรับดีใจ ---- */
    const cashDelta = g.players.map((p, i) => p.cash - (before.players[i]?.cash ?? p.cash))
    cashDelta.forEach((d, i) => {
      if (d <= -1500) say(i, 'sad')
      else if (d >= 1500) say(i, 'happy')
    })

    /* ---- ถูกกักกัน / โดนข้ามตา ---- */
    g.players.forEach((p, i) => {
      if (p.quarantine > before.players[i].quarantine || p.skipTurns > before.players[i].skipTurns) say(i, 'sad')
    })

    /* ---- การ์ดเหตุการณ์: เลือกโทนเสียงตามชนิดการ์ด ---- */
    const promptChanged = promptKey(g) !== promptKey(before)
    if (promptChanged && g.prompt) {
      if (g.prompt.t === 'event') {
        const c = g.prompt.card
        if (c.scope === 'global') add('globalEvent')
        else if (c.tone === 'good') add('buff')
        else add('debuff')
      } else if (g.prompt.t === 'deal') {
        add('card')
      } else if (g.prompt.t === 'jackpot' && g.prompt.amount > 0) {
        add('cashIn')
      }
    }

    /* ---- ถูกแช่แข็ง / เข้าสถานีกักกัน ---- */
    const frozen = g.players.some(
      (p, i) => p.quarantine > before.players[i].quarantine || p.skipTurns > before.players[i].skipTurns,
    )
    if (frozen) add('freeze')

    /* ---- ได้โล่พลังงาน ---- */
    if (g.players.some((p, i) => p.shield > before.players[i].shield)) add('buff')

    /* ---- ทอยลูกเต๋า ---- */
    if (!before.rolling && g.rolling) add('dice')
    if (before.rolling && !g.rolling && g.rollAgain) add('double')

    /* ---- ซื้อโฉนด / อัปเกรด / ปิดดีล ---- */
    const ownedBefore = before.tiles.filter((t) => t.ownerId !== null).length
    const ownedNow = g.tiles.filter((t) => t.ownerId !== null).length
    if (ownedNow > ownedBefore) add('buy')

    const levelsBefore = before.tiles.reduce((s, t) => s + t.level, 0)
    const levelsNow = g.tiles.reduce((s, t) => s + t.level, 0)
    if (levelsNow > levelsBefore) add('upgrade')

    if (g.players.some((p, i) => p.assets.length > before.players[i].assets.length)) add('buy')

    /* ---- เงินเข้า/ออกของคนที่กำลังเล่นอยู่ (คนที่จอกำลังโฟกัส) ---- */
    const focus = mySeat ?? before.current
    const cashBefore = before.players[focus]?.cash ?? 0
    const cashNow = g.players[focus]?.cash ?? 0
    const delta = cashNow - cashBefore
    if (Math.abs(delta) > 500 && !queue.includes('buy')) {
      add(delta > 0 ? 'cashIn' : 'cashOut')
    }

    /* ---- ถึงตาเราแล้ว (โหมดออนไลน์) ---- */
    if (mySeat !== null && before.current !== mySeat && g.current === mySeat && g.phase !== 'gameover') {
      add('yourTurn')
    }

    /* ---- ถึงตาใครก็ขานรับ (รวมบอทด้วย ฉากจะได้ไม่เงียบ) ---- */
    if (before.current !== g.current && g.phase !== 'gameover') say(g.current, 'ready')

    /* ---- ทอยเต๋า = เร่งเร้า ---- */
    if (!before.rolling && g.rolling) say(g.current, 'cheer')

    // จำกัดไม่ให้เสียงทับกันจนรก — เอาสองเสียงแรกตามลำดับความสำคัญพอ
    queue.slice(0, 2).forEach(play)
    voices.slice(0, 2).forEach((v) => speak(v.id, v.emotion))
  }, [g, mySeat])
}

function promptKey(g: GameState): string {
  const p = g.prompt
  if (!p) return '-'
  switch (p.t) {
    case 'event':
      return `event:${p.card.id}`
    case 'deal':
      return `deal:${p.card.id}`
    case 'buyProperty':
      return `buy:${p.tileId}`
    case 'payRent':
      return `rent:${p.tileId}:${p.amount}`
    case 'tax':
      return `tax:${p.tileId}:${p.amount}`
    case 'jackpot':
      return `jackpot:${p.amount}`
    case 'payday':
      return `payday:${p.amount}`
    case 'info':
      return `info:${p.title}`
    default:
      return p.t
  }
}
